/**
 * The single HTTP entry point of the player website.
 *
 * Site API auth is a session cookie (`mgr_session`) plus CSRF. This layer
 * handles: base URL, locale header, CSRF header, envelope unwrapping, error
 * normalization, and one transparent CSRF retry.
 *
 * Security note: this layer is about *transport*, not trust. Permissions,
 * points, ban state and feature flags are whatever the server says.
 */

import { csrfStore } from './csrf';
import { endpoints } from './endpoints';
import { ApiError } from './types';

export type ApiMode = 'mock' | 'live';

export const apiMode: ApiMode = import.meta.env.VITE_API_MODE === 'mock' ? 'mock' : 'live';

/** Same-origin by default so the SPA can sit behind Nginx next to the PHP app. */
export const apiBaseUrl: string = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';

export type QueryValue = string | number | boolean | null | undefined;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: Record<string, QueryValue>;
  body?: unknown;
  /** Public endpoints still send cookies so a live session is visible. */
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Skip the automatic CSRF fetch (used by GET /csrf itself). */
  skipCsrf?: boolean;
}

/**
 * A transport receives a fully resolved request and returns the raw JSON body
 * plus status. The mock adapter implements this interface, which is why every
 * query in the app already speaks real endpoint paths.
 */
export interface TransportRequest {
  method: string;
  /** Path relative to the API root, e.g. `/leaderboards/points`. */
  path: string;
  query: Record<string, string>;
  body: unknown;
  headers: Record<string, string>;
  signal?: AbortSignal;
}

export interface TransportResponse {
  status: number;
  body: unknown;
  headers?: Record<string, string>;
}

export type Transport = (request: TransportRequest) => Promise<TransportResponse>;

let transport: Transport | null = null;

export function setTransport(next: Transport | null) {
  transport = next;
}

/* ------------------------------------------------------------------- locale */

let currentLocale = 'zh';

export function setApiLocale(locale: string) {
  currentLocale = locale.startsWith('en') ? 'en' : 'zh';
}

export function getApiLocale() {
  return currentLocale;
}

/* ---------------------------------------------------------------- envelopes */

interface Envelope {
  ok?: boolean;
  data?: unknown;
  meta?: Record<string, unknown> | null;
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string | string[]> | null;
    request_id?: string | null;
    data?: Record<string, unknown> | null;
  } | null;
}

export interface EnvelopeResult<T> {
  data: T;
  meta: Record<string, unknown>;
}

const isEnvelope = (body: unknown): body is Envelope =>
  typeof body === 'object' && body !== null && ('ok' in body || 'data' in body || 'error' in body);

function flattenFields(fields: Record<string, string | string[]> | null | undefined): Record<string, string> {
  if (!fields) return {};
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(fields)) {
    result[key] = Array.isArray(value) ? value.filter(Boolean).join(' ') : String(value);
  }
  return result;
}

function fallbackCode(status: number): string {
  switch (status) {
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 422:
      return 'VALIDATION_FAILED';
    case 423:
      return 'ACCOUNT_LOCKED';
    case 429:
      return 'RATE_LIMITED';
    case 503:
      return 'MAINTENANCE_MODE';
    default:
      return 'REQUEST_FAILED';
  }
}

function toApiError(status: number, body: unknown, retryAfterHeader?: string | null): ApiError {
  const envelope = isEnvelope(body) ? body : undefined;
  const error = envelope?.error ?? undefined;
  const extra = error?.data && typeof error.data === 'object' ? error.data : null;
  const retryAfter =
    (typeof extra?.retry_after === 'number' ? extra.retry_after : null) ??
    (retryAfterHeader && Number.isFinite(Number(retryAfterHeader)) ? Number(retryAfterHeader) : null);
  return new ApiError({
    status,
    code: error?.code ?? fallbackCode(status),
    message: error?.message ?? `Request failed with status ${status}`,
    fieldErrors: flattenFields(error?.fields),
    retryAfterSeconds: retryAfter,
    requestId: error?.request_id ?? null,
    data: extra,
  });
}

/* ------------------------------------------------------------------ dispatch */

function isFormData(value: unknown): value is FormData {
  return typeof FormData !== 'undefined' && value instanceof FormData;
}

function baseHeaders(hasJsonBody: boolean, extra?: Record<string, string>) {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Accept-Language': currentLocale === 'en' ? 'en' : 'zh-CN',
    'X-Requested-With': 'XMLHttpRequest',
    ...extra,
  };
  if (hasJsonBody) headers['Content-Type'] = 'application/json';
  const csrf = csrfStore.get();
  if (csrf) headers['X-CSRF-Token'] = csrf;
  return headers;
}

async function dispatch(request: Omit<TransportRequest, 'query'> & { query: Record<string, string> }) {
  if (transport) return transport(request);

  const search = new URLSearchParams(request.query).toString();
  const url = `${apiBaseUrl}${request.path}${search ? `?${search}` : ''}`;
  const isMultipart = isFormData(request.body);
  let response: Response;
  try {
    response = await fetch(url, {
      method: request.method,
      headers: request.headers,
      body:
        request.body === undefined || request.body === null
          ? undefined
          : isMultipart
            ? (request.body as FormData)
            : JSON.stringify(request.body),
      signal: request.signal,
      credentials: 'include',
    });
  } catch (cause) {
    if ((cause as Error)?.name === 'AbortError') throw cause;
    throw new ApiError({
      status: 0,
      code: 'NETWORK_ERROR',
      message: 'Network request failed',
    });
  }

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text) as unknown;
    } catch {
      body = { message: text };
    }
  }
  const headerMap: Record<string, string> = {};
  response.headers.forEach((value, key) => {
    headerMap[key.toLowerCase()] = value;
  });
  return { status: response.status, body, headers: headerMap };
}

function captureCsrf(data: unknown) {
  if (data && typeof data === 'object' && 'csrf_token' in data) {
    const token = (data as { csrf_token?: unknown }).csrf_token;
    if (typeof token === 'string' && token.length > 0) csrfStore.set(token);
  }
}

let csrfInFlight: Promise<void> | null = null;

export async function ensureCsrf(): Promise<void> {
  if (csrfStore.get()) return;
  csrfInFlight ??= (async () => {
    try {
      const response = await dispatch({
        method: 'GET',
        path: endpoints.csrf(),
        query: { lang: currentLocale },
        body: undefined,
        headers: baseHeaders(false),
      });
      const envelope = isEnvelope(response.body) ? response.body : undefined;
      captureCsrf(envelope?.data ?? response.body);
    } finally {
      csrfInFlight = null;
    }
  })();
  await csrfInFlight;
}

export function acceptCsrfToken(token: string | null | undefined) {
  if (typeof token === 'string' && token.length > 0) csrfStore.set(token);
}

/* ------------------------------------------------------------------- public */

export async function requestEnvelope<T = unknown>(
  path: string,
  options: RequestOptions = {},
): Promise<EnvelopeResult<T>> {
  const { method = 'GET', query, body, signal, headers, skipCsrf } = options;
  const mutating = method !== 'GET';

  if (mutating && !skipCsrf) await ensureCsrf();

  const normalizedQuery: Record<string, string> = { lang: currentLocale };
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === null || value === undefined || value === '') continue;
    normalizedQuery[key] = String(value);
  }

  const send = () =>
    dispatch({
      method,
      path,
      query: normalizedQuery,
      body,
      headers: baseHeaders(body !== undefined && body !== null && !isFormData(body), headers),
      signal,
    });

  let response = await send();

  if (response.status === 403) {
    const err = toApiError(response.status, response.body, response.headers?.['retry-after']);
    if (err.isCsrfMismatch && !skipCsrf) {
      csrfStore.clear();
      await ensureCsrf();
      response = await send();
    }
  }

  if (response.status >= 400) {
    throw toApiError(response.status, response.body, response.headers?.['retry-after']);
  }

  const envelope = isEnvelope(response.body) ? response.body : undefined;
  if (envelope && envelope.ok === false) throw toApiError(response.status, response.body);
  const data = (envelope && 'data' in envelope ? envelope.data : response.body) as T;
  captureCsrf(data);
  return { data, meta: (envelope?.meta as Record<string, unknown> | undefined) ?? {} };
}

export async function request<T = unknown>(path: string, options: RequestOptions = {}): Promise<T> {
  const { data } = await requestEnvelope<T>(path, options);
  return data;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  getEnvelope: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    requestEnvelope<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body: body ?? {} }),
  postEnvelope: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    requestEnvelope<T>(path, { ...options, method: 'POST', body: body ?? {} }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body: body ?? {} }),
  put: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PUT', body: body ?? {} }),
  delete: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method'>) =>
    request<T>(path, { ...options, method: 'DELETE', body }),
};

export function newClientRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return `mgr-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
